/**
 * 节流与防抖 Hooks
 */

import { useCallback, useRef, useEffect } from 'react';

/**
 * 节流 Hook
 * @param fn 要执行的函数
 * @param delay 节流时间（毫秒）
 */
export function useThrottle<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): T {
  const lastRunRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  
  const throttled = useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now();
      const timeSinceLastRun = now - lastRunRef.current;
      
      if (timeSinceLastRun >= delay) {
        lastRunRef.current = now;
        fn(...args);
      } else {
        // 清除之前的定时器
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        // 设置新的定时器
        timeoutRef.current = setTimeout(() => {
          lastRunRef.current = Date.now();
          fn(...args);
        }, delay - timeSinceLastRun);
      }
    },
    [fn, delay]
  ) as T;
  
  // 清理
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return throttled;
}

/**
 * 防抖 Hook
 * @param fn 要执行的函数
 * @param delay 防抖时间（毫秒）
 */
export function useDebounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): T {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  
  const debounced = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        fn(...args);
      }, delay);
    },
    [fn, delay]
  ) as T;
  
  // 清理
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return debounced;
}

/**
 * 带立即执行选项的防抖
 */
export function useDebounceWithImmediate<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number,
  immediate: boolean = false
): T {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const immediateExecutedRef = useRef(false);
  
  const debounced = useCallback(
    (...args: Parameters<T>) => {
      if (immediate && !immediateExecutedRef.current) {
        immediateExecutedRef.current = true;
        fn(...args);
      }
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        if (!immediate) {
          fn(...args);
        }
        immediateExecutedRef.current = false;
      }, delay);
    },
    [fn, delay, immediate]
  ) as T;
  
  // 清理
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return debounced;
}